import { useMemo } from 'react';

interface RadarChartProps {
  data: Record<string, number>;
  labels: Record<string, string>;
  size?: number;
  color?: string;
  bgColor?: string;
}

export default function RadarChart({
  data,
  labels,
  size = 220,
  color = '#1a56db',
  bgColor = 'rgba(26,86,219,0.12)'
}: RadarChartProps) {
  const keys = useMemo(() => Object.keys(data), [data]);
  const n = keys.length;
  const cx = size / 2;
  const cy = size / 2;
  const maxR = (size / 2) * 0.68;
  const labelR = (size / 2) * 0.88;

  const getAngle = (i: number) => (i * 2 * Math.PI) / n - Math.PI / 2;

  const getPoint = (i: number, r: number) => ({
    x: cx + r * Math.cos(getAngle(i)),
    y: cy + r * Math.sin(getAngle(i)),
  });

  const rings = [0.25, 0.5, 0.75, 1.0];

  const ringPolygons = rings.map(ring =>
    keys.map((_, i) => getPoint(i, maxR * ring)).map(p => `${p.x},${p.y}`).join(' ')
  );

  const dataPoints = keys.map((key, i) =>
    getPoint(i, (Math.max(0, Math.min(100, data[key] || 0)) / 100) * maxR)
  );
  const polygonPoints = dataPoints.map(p => `${p.x},${p.y}`).join(' ');

  const getTextAnchor = (i: number) => {
    const angle = getAngle(i);
    const cos = Math.cos(angle);
    if (cos > 0.3) return 'start';
    if (cos < -0.3) return 'end';
    return 'middle';
  };

  const getIconForLabel = (key: string) => {
    const icons: Record<string, string> = {
      timeManagement: '⏰',
      studyFocus: '📚',
      lifeCare: '🏠',
      sportsHealth: '🏃',
      responsibility: '💪',
      etiquette: '🤝',
    };
    return icons[key] || '●';
  };

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="overflow-visible">
      {/* Background rings */}
      {ringPolygons.map((pts, ri) => (
        <polygon
          key={ri}
          points={pts}
          fill={ri === 3 ? 'rgba(241,245,255,0.8)' : 'none'}
          stroke={ri === 3 ? '#dbeafe' : '#e5e7eb'}
          strokeWidth={ri === 3 ? '1.5' : '1'}
        />
      ))}

      {/* Axis lines */}
      {keys.map((_, i) => {
        const outer = getPoint(i, maxR);
        return (
          <line
            key={i}
            x1={cx}
            y1={cy}
            x2={outer.x}
            y2={outer.y}
            stroke="#d1d5db"
            strokeWidth="1"
            strokeDasharray="3,3"
          />
        );
      })}

      {/* Data polygon */}
      <polygon
        points={polygonPoints}
        fill={bgColor}
        stroke={color}
        strokeWidth="2"
        strokeLinejoin="round"
      />

      {/* Data dots */}
      {dataPoints.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="4" fill={color} stroke="white" strokeWidth="1.5" />
      ))}

      {/* Labels */}
      {keys.map((key, i) => {
        const labelPt = getPoint(i, labelR);
        const anchor = getTextAnchor(i);
        return (
          <g key={i}>
            <text
              x={labelPt.x}
              y={labelPt.y - 6}
              textAnchor={anchor}
              fontSize="9"
              fill="#6b7280"
              fontFamily="sans-serif"
            >
              {getIconForLabel(key)}
            </text>
            <text
              x={labelPt.x}
              y={labelPt.y + 6}
              textAnchor={anchor}
              fontSize="9"
              fill="#374151"
              fontWeight="500"
              fontFamily="sans-serif"
            >
              {labels[key] || key}
            </text>
            <text
              x={labelPt.x}
              y={labelPt.y + 17}
              textAnchor={anchor}
              fontSize="9"
              fill={color}
              fontWeight="700"
              fontFamily="sans-serif"
            >
              {data[key]}%
            </text>
          </g>
        );
      })}
    </svg>
  );
}
